import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Dimensions,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { useIsFocused } from "@react-navigation/native";
import { useDispatch, useSelector } from "react-redux";
import {
  reportHistoryHeaders,
  mdReportHistoryHeaders,
  smReportHistoryHeaders,
} from "./issueHeaders";
import { FAB, DataTable } from "react-native-paper";
import { Ionicons, Feather } from "@expo/vector-icons";
import {
  setIssueCategoryFilter,
  setIssuePage,
  setIssueSearchQuery,
  setIssueType,
} from "../../../redux/reducers/portalReducer";
import { getPortalReportHistory } from "../../../redux/dispatcher/portalDispatcher";
import { DynamicLabelBannerWithSearchBar } from "../../../components/custom/Banners/DynamicLabelBannerWithSearchBar";
import { PortalNestedDataTable } from "../../../components/custom/DataTable/PortalNestedDataTable";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import Spinner from "../../../components/shared/Spinner";
import { useAppTheme } from "../../../hooks/useAppTheme";
import { ThemeColors } from "../../../utils/constants/colors";
import { formatValueAsDataType } from "../../../utils/common";
import statusColorCodes from "../../../utils/constants/statusColorCodes";

export default function CustomerIssueScreen({ navigation }: any) {
  const isFocused = useIsFocused();
  const dispatch = useDispatch<any>();
  const { colors, isDark } = useAppTheme();
  const styles = React.useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const cardStyles = React.useMemo(() => createCardStyles(colors, isDark), [colors, isDark]);

  // Get portal state from Redux
  const {
    orders,
    loading,
    searching,
    totalCount,
    totalPages,
    page,
    pageSize,
    searchQuery,
    categoryFilter,
    sortBy,
    issueType,
  } = useSelector((state: any) => state.portal.issues);

  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      reportHistoryHeaders,
    );

  const { isMobileDevice, isSmallDevice, isMediumDevice, isLargeDevice } =
    useContext(DeviceOrientationContext);

  const STATUS_FILTERS = ["all", "OPEN", "CLOSE"];

  const ISSUE_TYPE_FILTERS = [
    "all",
    "DAMAGED_ITEM",
    "MISSING_ITEM",
    "WRONG_ITEM",
    "CANCEL_ORDER",
    "OTHER",
  ];

  // Display label: strip underscores for UI, keep raw value for dispatch
  const formatFilterLabel = (value: string) =>
    value === "all" ? "All" : value.replace(/_/g, " ");

  // Separate local state for each filter group
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedIssueTypes, setSelectedIssueTypes] = useState<string[]>([]);
  const [mobileFilterOpen, setMobileFilterOpen] = useState<string | null>(null);
  const [showMobileFilters, setShowMobileFilters] = useState<boolean>(false);

  const handleStatusChange = (option: string) => {
    if (option === "all") {
      setSelectedStatuses([]);
      dispatch(setIssueCategoryFilter("all"));
    } else {
      const updated = selectedStatuses.includes(option)
        ? selectedStatuses.filter((s) => s !== option)
        : [...selectedStatuses, option];
      setSelectedStatuses(updated);
      dispatch(
        setIssueCategoryFilter(
          updated.length === 0 ? "all" : updated.join(","),
        ),
      );
    }
    dispatch(setIssuePage(1));
  };

  const handleIssueTypeChange = (option: string) => {
    if (option === "all") {
      setSelectedIssueTypes([]);
      dispatch(setIssueType("all"));
    } else {
      const updated = selectedIssueTypes.includes(option)
        ? selectedIssueTypes.filter((t) => t !== option)
        : [...selectedIssueTypes, option];
      setSelectedIssueTypes(updated);
      dispatch(setIssueType(updated.length === 0 ? "all" : updated.join(",")));
    }
    dispatch(setIssuePage(1));
  };

  const renderFilters = (isMobileFilters: boolean = false) => (
    <View
      style={[
        styles.quickFilterBar,
        isMobileFilters && styles.quickFilterBarMobile,
      ]}
    >
      <ScrollView
        horizontal
        style={{ width: "100%" }}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={[
          styles.quickFilterChipsContainer,
          isMobileFilters && styles.quickFilterChipsContainerMobile,
        ]}
      >
        {!isMobileFilters && (
          <Text style={styles.quickFilterLabel}>Quick Filter:</Text>
        )}

        <TouchableOpacity
          style={[
            styles.mobileDropdownButton,
            mobileFilterOpen === "status" && styles.mobileDropdownButtonActive,
          ]}
          onPress={() => setMobileFilterOpen(mobileFilterOpen === "status" ? null : "status")}
        >
          <Text style={styles.mobileDropdownLabel}>Status: </Text>
          <Text style={styles.mobileDropdownValue}>
            {selectedStatuses.length === 0
              ? "All"
              : selectedStatuses.length === 1
              ? formatFilterLabel(selectedStatuses[0])
              : `${selectedStatuses.length} selected`}
          </Text>
          <Text style={styles.mobileDropdownArrow}>{mobileFilterOpen === "status" ? "▲" : "▼"}</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.mobileDropdownButton,
            mobileFilterOpen === "type" && styles.mobileDropdownButtonActive,
            { marginLeft: 8 },
          ]}
          onPress={() => setMobileFilterOpen(mobileFilterOpen === "type" ? null : "type")}
        >
          <Text style={styles.mobileDropdownLabel}>Type: </Text>
          <Text style={styles.mobileDropdownValue}>
            {selectedIssueTypes.length === 0
              ? "All"
              : selectedIssueTypes.length === 1
              ? formatFilterLabel(selectedIssueTypes[0])
              : `${selectedIssueTypes.length} selected`}
          </Text>
          <Text style={styles.mobileDropdownArrow}>{mobileFilterOpen === "type" ? "▲" : "▼"}</Text>
        </TouchableOpacity>
      </ScrollView>

      {mobileFilterOpen === "status" && (
        <ScrollView
          horizontal
          style={{ width: "100%" }}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={[
            styles.mobileChipRow,
            isMobileFilters && styles.mobileChipRowMobile,
            { marginTop: 2 },
          ]}
        >
          {STATUS_FILTERS.map((option) => {
            const isActive =
              option === "all"
                ? selectedStatuses.length === 0
                : selectedStatuses.includes(option);
            return (
              <TouchableOpacity
                key={option}
                style={[styles.filterChip, isActive && styles.filterChipActive]}
                onPress={() => handleStatusChange(option)}
              >
                <Text
                  style={
                    isActive
                      ? styles.filterChipTextActive
                      : styles.filterChipText
                  }
                >
                  {formatFilterLabel(option)}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      )}

      {mobileFilterOpen === "type" && (
        <ScrollView
          horizontal
          style={{ width: "100%" }}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={[
            styles.mobileChipRow,
            isMobileFilters && styles.mobileChipRowMobile,
            { marginTop: 2 },
          ]}
        >
          {ISSUE_TYPE_FILTERS.map((option) => {
            const isActive =
              option === "all"
                ? selectedIssueTypes.length === 0
                : selectedIssueTypes.includes(option);
            return (
              <TouchableOpacity
                key={option}
                style={[styles.filterChip, isActive && styles.filterChipActive]}
                onPress={() => handleIssueTypeChange(option)}
              >
                <Text
                  style={
                    isActive
                      ? styles.filterChipTextActive
                      : styles.filterChipText
                  }
                >
                  {formatFilterLabel(option)}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      )}
    </View>
  );

  const listRef = useRef<ScrollView>(null);
  const [contentOffSet, setContentOffSet] = useState<number>(0);
  const [fabPosition] = useState<number>(320);
  const [isAtBottom, setIsAtBottom] = useState<boolean>(false);

  const handleScroll = (event: any) => {
    const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
    setContentOffSet(contentOffset.y);

    const isCloseToBottom =
      layoutMeasurement.height + contentOffset.y >= contentSize.height - 200;
    setIsAtBottom(isCloseToBottom);
  };

  const navigateToOrder = (order: any, action: string) => {
    navigation.navigate("CustomerIssueDetailsReport", { orderId: order._id });
  };

  // Fetch orders on mount and when filters change
  useEffect(() => {
    if (isFocused) {
      dispatch(getPortalReportHistory() as any).unwrap();
    }
  }, [
    isFocused,
    dispatch,
    page,
    searchQuery,
    categoryFilter,
    sortBy,
    issueType,
  ]);

  // Update table headers based on device size
  useEffect(() => {
    setTableHeaders(
      isLargeDevice
        ? reportHistoryHeaders
        : isMediumDevice
          ? mdReportHistoryHeaders
          : smReportHistoryHeaders,
    );
  }, [isLargeDevice, isMediumDevice, isSmallDevice]);

  // Handle search
  const handleSearch = (query: string) => {
    dispatch(setIssueSearchQuery(query));
    dispatch(setIssuePage(0)); // reset page on search
  };

  const handleSearchTextChange = (text: string) => {
    dispatch(setIssueSearchQuery(text));
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    dispatch(setIssuePage(newPage));
  };

  // Handle refresh
  const handleRefresh = () => {
    dispatch(getPortalReportHistory() as any);
  };

  const columnsToNotHaveThousandSeparator: Array<string> = [
    "Packing #",
    "Hubspot ID",
    "Document Number",
    "Customer Number",
    "Order No",
    "Ticket No",
  ];

  const MobileIssueCard = ({ order }: { order: any }) => {
    return (
      <TouchableOpacity
        style={cardStyles.card}
        onPress={() => navigateToOrder(order, "view")}
        activeOpacity={0.7}
      >
        <View style={cardStyles.cardHeader}>
          <View style={cardStyles.cardHeaderLeft}>
            <View style={cardStyles.containerIconWrap}>
              <Ionicons name="alert-circle-outline" size={18} color={isDark ? colors.primary : "#3B82F6"} />
            </View>
            <View style={{ flex: 1, backgroundColor: "transparent" }}>
              <Text style={cardStyles.containerNumber} numberOfLines={1} ellipsizeMode="tail">
                {order.ticketNo}
              </Text>
              {!!order.orderNo && (
                <Text style={cardStyles.containerSubtitle} numberOfLines={1} ellipsizeMode="tail">
                  Order: {order.orderNo}
                </Text>
              )}
            </View>
          </View>
          {!!order.status && (
            <View
              style={[
                cardStyles.statusBadge,
                { backgroundColor: (statusColorCodes[order.status] || colors.textSecondary) + "1A" },
              ]}
            >
              <View
                style={[
                  cardStyles.statusDot,
                  { backgroundColor: statusColorCodes[order.status] || colors.textSecondary },
                ]}
              />
              <Text
                style={[
                  cardStyles.statusBadgeText,
                  { color: statusColorCodes[order.status] || colors.textSecondary },
                ]}
                numberOfLines={1}
              >
                {order.status}
              </Text>
            </View>
          )}
        </View>

        <View style={cardStyles.divider} />

        <View style={cardStyles.cardFooter}>
          {!!order.issueType && (
            <View style={cardStyles.metaChip}>
              <Ionicons name="pricetag-outline" size={12} color={isDark ? colors.textSecondary : "#64748B"} />
              <Text style={cardStyles.metaChipText}>{formatFilterLabel(order.issueType)}</Text>
            </View>
          )}
          {!!order.createdByEmail && (
            <View style={cardStyles.metaChip}>
              <Ionicons name="person-outline" size={12} color={isDark ? colors.textSecondary : "#64748B"} />
              <Text style={cardStyles.metaChipText} numberOfLines={1} ellipsizeMode="tail">{order.createdByEmail}</Text>
            </View>
          )}
          {!!order.createdAt && (
            <View style={cardStyles.metaChip}>
              <Ionicons name="calendar-outline" size={12} color={isDark ? colors.textSecondary : "#64748B"} />
              <Text style={cardStyles.metaChipText}>{formatValueAsDataType(order.createdAt, false)}</Text>
            </View>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.container}>
      {isSmallDevice || isMobileDevice ? (
        <>
          {/* Mobile: Search bar + filter toggle in a card */}
          <View style={styles.mobileFilterCard}>
            <DynamicLabelBannerWithSearchBar
              titleName={`Total Items: ${totalCount || 0}`}
              setRefresh={handleRefresh}
              dataToExport={orders}
              fileName="issuesHistory"
              sheetName="Issues"
              disableTitle={false}
              noBorder
              handleSearch={handleSearch}
              onSearchTextChange={handleSearchTextChange}
              _searchText={searchQuery}
              middleSearchControls={
                <TouchableOpacity
                  style={[
                    styles.mobileFilterToggle,
                    showMobileFilters && styles.mobileFilterToggleActive,
                  ]}
                  onPress={() => setShowMobileFilters(!showMobileFilters)}
                >
                  <Feather
                    name="filter"
                    size={15}
                    color={showMobileFilters ? "#FFFFFF" : "#6B7280"}
                  />
                </TouchableOpacity>
              }
            />
            {/* Mobile: Dropdown filter button */}
            {showMobileFilters && renderFilters(true)}
          </View>
        </>
      ) : (
        /* Desktop: Quick Filter chips on left + search on right */
        <View style={styles.desktopFilterRow}>
          <View style={{ flex: 1 }}>
            {renderFilters()}
          </View>
          <View style={styles.quickFilterSearchSection}>
            <DynamicLabelBannerWithSearchBar
              titleName={`Total Items: ${totalCount || 0}`}
              setRefresh={handleRefresh}
              dataToExport={orders}
              fileName="issuesHistory"
              sheetName="Issues"
              disableTitle={false}
              noBorder
              handleSearch={handleSearch}
              onSearchTextChange={handleSearchTextChange}
              _searchText={searchQuery}
            />
          </View>
        </View>
      )}

      <ScrollView
        style={{
          flex: 1,
          backgroundColor:
            isSmallDevice || isMobileDevice
              ? isDark
                ? colors.background
                : "#eef2f6"
              : colors.background,
        }}
        contentContainerStyle={{ flexGrow: 0 }}
        onScroll={handleScroll}
        ref={listRef}
      >
        {loading && !searching ? (
          <View style={styles.spinnerContainer}>
            <Spinner size="small" color={colors.primary} />
          </View>
        ) : (
          <View
            style={{
              backgroundColor:
                isSmallDevice || isMobileDevice
                  ? "transparent"
                  : colors.surface,
            }}
          >
            {isAtBottom && !isSmallDevice && !isMobileDevice && (
              // @ts-ignore - React Native 0.71+ types issue with react-native-paper FAB
              <FAB
                icon="arrow-up"
                style={[
                  styles.fab,
                  {
                    top:
                      contentOffSet +
                      Dimensions.get("window").height -
                      fabPosition -
                      120,
                  },
                ]}
                onPress={() => {
                  if (listRef.current) {
                    listRef.current.scrollTo({ y: 0, animated: true });
                  }
                }}
                small
              />
            )}
            {isSmallDevice || isMobileDevice ? (
              searching ? (
                <View style={styles.spinnerContainer}>
                  <Spinner size="small" color={colors.primary} />
                </View>
              ) : (
                <View style={cardStyles.cardList}>
                  {(orders || []).length === 0 ? (
                    <View style={cardStyles.emptyState}>
                      <Ionicons name="alert-circle-outline" size={48} color="#CBD5E1" />
                      <Text style={cardStyles.emptyStateText}>No data available</Text>
                    </View>
                  ) : (
                    <>
                      {orders.map((order: any, index: number) => (
                        <MobileIssueCard
                          key={order._id || `${order.ticketNo || "issue"}-${index}`}
                          order={order}
                        />
                      ))}
                      <DataTable.Pagination
                        page={page - 1}
                        numberOfPages={totalPages}
                        onPageChange={(newPage) => handlePageChange(newPage + 1)}
                        label={`${(page - 1) * pageSize + 1}-${Math.min(
                          page * pageSize,
                          totalCount,
                        )} of ${totalCount}`}
                        showFastPaginationControls
                      />
                    </>
                  )}
                </View>
              )
            ) : (
              <PortalNestedDataTable
                itemsPerPage={pageSize}
                isNestedDataLoaded={!loading}
                nestedHeader={tableHeaders}
                nestedRows={orders}
                rowKeyValue={"documentNumber"}
                key="rowKeyValue"
                nestedColumnsToNotHaveThousandSeparator={
                  columnsToNotHaveThousandSeparator
                }
                isDisplayTimeWithDate={false}
                handleNestedSortColumn={() => {}}
                disableHeader={false}
                operationActions={navigateToOrder}
                isDetail={false}
                setModalVisible={() => {}}
                handleRowClick={() => {}}
                setSelected={() => {}}
                // Server-side pagination props
                useServerPagination={true}
                currentPage={page}
                totalPages={totalPages}
                totalCount={totalCount}
                onPageChange={handlePageChange}
                loading={searching}
                allowDefaultView={true}
              />
            )}
          </View>
        )}
      </ScrollView>
    </View>
  );
}

const createStyles = (colors: ThemeColors, isDark: boolean) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: isDark ? colors.background : "#eef2f6",
  },
  desktopFilterRow: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: colors.surface,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderSubtle,
  },
  quickFilterBar: {
    flexDirection: "column",
    alignItems: "flex-start",
    backgroundColor: colors.surface,
    paddingVertical: 4,
    paddingHorizontal: 0,
  },
  quickFilterBarMobile: {
    paddingBottom: 8,
  },
  quickFilterChipsContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingRight: 12,
  },
  quickFilterChipsContainerMobile: {
    paddingLeft: 12,
  },
  quickFilterLabel: {
    fontWeight: "700",
    fontSize: 13,
    color: colors.text,
    marginRight: 10,
  },
  quickFilterSearchSection: {
    flexShrink: 0,
    minWidth: 350,
  },
  mobileFilterCard: {
    marginHorizontal: 10,
    marginTop: 10,
    marginBottom: 8,
    backgroundColor: isDark ? colors.surface : "#FFFFFF",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: isDark ? colors.border : "#E2E8F0",
    overflow: "hidden" as any,
    shadowColor: isDark ? colors.text : "#0F172A",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.07,
    shadowRadius: 12,
    elevation: 4,
  },
  mobileFilterToggle: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: colors.surfaceSubtle,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    borderColor: colors.border,
  },
  mobileFilterToggleActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  mobileFilterWrapper: {
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  mobileDropdownButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: isDark ? colors.surfaceSubtle : "#F1F5F9",
    borderRadius: 999,
    borderWidth: 1,
    borderColor: colors.borderSubtle,
    alignSelf: "flex-start",
  },
  mobileDropdownButtonActive: {
    borderColor: colors.primary,
    backgroundColor: isDark ? colors.primary + "20" : "#EFF6FF",
  },
  mobileDropdownLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: colors.textSecondary,
  },
  mobileDropdownValue: {
    fontSize: 12,
    fontWeight: "700",
    color: colors.primary,
  },
  mobileDropdownArrow: {
    fontSize: 8,
    color: colors.textSecondary,
    marginLeft: 4,
  },
  mobileChipRow: {
    flexDirection: "row",
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  mobileChipRowMobile: {
    paddingLeft: 12,
    paddingRight: 24,
  },
  contentContainer: {
    flex: 1,
    backgroundColor: colors.background,
  },
  spinnerContainer: {
    justifyContent: "center",
    alignItems: "center",
    marginTop: 20,
  },
  fab: {
    position: "absolute",
    margin: 16,
    right: 0,
    zIndex: 1,
  },
  filterChip: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    backgroundColor: isDark ? colors.surfaceSubtle : "#F1F5F9",
    borderRadius: 999,
    marginRight: 8,
    marginBottom: 6,
    borderWidth: 1,
    borderColor: colors.borderSubtle,
  },
  filterChipActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  filterChipText: {
    fontSize: 13,
    color: colors.textSecondary,
    fontWeight: "500" as const,
  },
  filterChipTextActive: {
    color: "#fff",
    fontSize: 13,
    fontWeight: "600" as const,
  },
  filterRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 4,
  },
});

const createCardStyles = (colors: ThemeColors, isDark: boolean) => StyleSheet.create({
  cardList: {
    paddingHorizontal: 16,
    paddingBottom: 24,
    paddingTop: 8,
    gap: 12,
    backgroundColor: "transparent",
  },
  card: {
    backgroundColor: isDark ? colors.surface : "#FFFFFF",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: isDark ? colors.border : "#E8EDF5",
    shadowColor: isDark ? colors.text : "#1E293B",
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.07,
    shadowRadius: 10,
    elevation: 3,
    overflow: "hidden" as any,
    marginBottom: 4,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 14,
    backgroundColor: isDark ? colors.surfaceSubtle : "#FAFBFF",
    gap: 8,
  },
  cardHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    backgroundColor: "transparent",
    flex: 1,
    paddingRight: 10,
  },
  containerIconWrap: {
    width: 34,
    height: 34,
    borderRadius: 10,
    backgroundColor: isDark ? colors.primary + "33" : "#E1F0FA",
    alignItems: "center",
    justifyContent: "center",
  },
  containerNumber: {
    fontSize: 14,
    fontWeight: "700" as const,
    color: isDark ? colors.text : "#0F172A",
    letterSpacing: 0.2,
    backgroundColor: "transparent",
  },
  containerSubtitle: {
    fontSize: 12,
    color: isDark ? colors.textSecondary : "#64748B",
    marginTop: 2,
    backgroundColor: "transparent",
  },
  statusBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    alignSelf: "flex-start",
    maxWidth: 160,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  statusBadgeText: {
    fontSize: 11,
    fontWeight: "600" as const,
    flexShrink: 1,
  },
  divider: {
    height: 1,
    backgroundColor: isDark ? colors.background : "#eef2f6",
    marginHorizontal: 16,
  },
  cardFooter: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap" as any,
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 8,
  },
  metaChip: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: isDark ? colors.surface : "#F8FAFC",
    borderRadius: 6,
    paddingVertical: 4,
    paddingHorizontal: 8,
    gap: 4,
    borderWidth: 1,
    borderColor: isDark ? colors.border : "#E2E8F0",
    flexShrink: 1,
    maxWidth: "100%",
  },
  metaChipText: {
    fontSize: 11,
    color: isDark ? colors.textSecondary : "#6B7280",
    fontWeight: "500" as const,
    flexShrink: 1,
  },
  emptyState: {
    alignItems: "center",
    paddingVertical: 60,
    gap: 12,
  },
  emptyStateText: {
    fontSize: 15,
    color: "#CBD5E1",
    fontWeight: "500" as const,
  },
});
